import { Box, Flex, Skeleton } from "@chakra-ui/react";
import useAssembleia from "features/assembleia/hooks/useAssembleia";
import { useAssembleiaRelatorioParticipantes } from "features/assembleia/hooks";
import { VotacaoParticipanteResponse } from "features/assembleia/typings";
import { useBreadcrumb } from "features/breadcrumbs";
import colors from "core/resources/theme/colors";
import { TabsDetalheAssembleiaRoutes } from "../../typings";
import ListaParticipantes from "./components/ListaParticipantes";
import { useMemo, useCallback } from "react";
import { Row, Col } from "antd";
import Card1Horizontal from "components/Cards/Card1Horizontal";
import { FaUsers, FaPrint } from "react-icons/fa";
import PrimaryButton from "components/Button/PrimaryButton";
import Container from "components/Container";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFRelatorio from "../Participantes/components/PDFRelatorio";

interface AssembleiaRelatoriosProps {
  idAssembleia: string;
}

function AssembleiaRelatorios({ idAssembleia }: AssembleiaRelatoriosProps) {
  const {
    data: detalheAssembleia,
    isLoading,
    isFetching,
  } = useAssembleia(idAssembleia);

  const {
    data,
    isLoading: _isLoadingParticipantes,
    isFetching: isFetchingRelatorio,
  } = useAssembleiaRelatorioParticipantes(idAssembleia, { limit: 9999 });

  const _isLoading = isLoading || isFetching;

  useBreadcrumb([
    { name: "Votações", url: "/administracao/assembleias" },
    {
      name:
        _isLoading || !detalheAssembleia
          ? "Carregando"
          : detalheAssembleia.nome,
      url: `/administracao/assembleias/${idAssembleia}/`,
    },
    {
      name: "Relatório de Participantes",
      url: `/administracao/assembleias/${idAssembleia}?opcao=${TabsDetalheAssembleiaRoutes.Relatorio}`,
    },
  ]);

  const participantes = useMemo(() => {
    return (
      data?.pages.reduce(
        (agg: VotacaoParticipanteResponse[], item) =>
          agg.concat(item.items ?? []),
        []
      ) ?? []
    );
  }, [data]);

  const { onDownload: _onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de participantes - ${detalheAssembleia?.nome}`,
    Component: PDFRelatorio,
    loading: isLoading,
  });

  const handlePrint = useCallback(() => {
    _onExportPdf({
      participantes,
      votacaoNome: detalheAssembleia!.nome,
    });
  }, [_onExportPdf, detalheAssembleia, participantes]);

  return (
    <Container pl={0} pb={2}>
      <Box mb="16px" className="hidden-print">
        <Row gutter={16}>
          <Col sm={8}>
            <Card1Horizontal
              bg={colors.blue100}
              color={colors.blue}
              description="Participantes"
              icon={<FaUsers fontSize="24px" color={colors.purple} />}
              title={(data?.pages[0].meta!.totalItems ?? 0).toString()}
              loading={false}
            />
          </Col>
          <Col sm={16}>
            <Flex justifyContent="flex-end" pt="60px" flex={1}>
              <PrimaryButton
                isLoading={isFetchingRelatorio}
                onClick={handlePrint}
              >
                <FaPrint /> Imprimir PDF
              </PrimaryButton>
            </Flex>
          </Col>
        </Row>
      </Box>

      {_isLoadingParticipantes ? (
        <Flex gridGap={3} flexDir="column">
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </Flex>
      ) : (
        <ListaParticipantes participantes={participantes} />
      )}
    </Container>
  );
}

const LoadingSkeleton = () => <Skeleton w="100%" rounded={16} h="70px" />;

export default AssembleiaRelatorios;
